import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { EventAttendance, EventAttendanceStatus } from '../models/event';
import { AttendanceUser } from '../sessions/session/attendance/attendance-ui-models';
import { URLService } from './dynamic-url.service';

export interface AttendanceStatusMetadata {
  statusCode: EventAttendanceStatus;
  displayName: string;
}

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root',
})
export class AttendanceService {
  private _eventAttendanceUpdate$ = new Subject<Partial<EventAttendance> | null>();

  attendanceStatusMetadata: AttendanceStatusMetadata[] = [
    {
      statusCode: EventAttendanceStatus.PRESENT,
      displayName: this.translate.instant('Present'),
    },
    {
      statusCode: EventAttendanceStatus.LATE,
      displayName: this.translate.instant('Late'),
    },
    {
      statusCode: EventAttendanceStatus.ABSENT,
      displayName: this.translate.instant('Absent'),
    },
    {
      statusCode: EventAttendanceStatus.LEFT,
      displayName: this.translate.instant('Left'),
    },
    {
      statusCode: EventAttendanceStatus.EXCUSED,
      displayName: this.translate.instant('Excused'),
    },
    {
      statusCode: EventAttendanceStatus.CANCELLED,
      displayName: this.translate.instant('Cancelled'),
    },
  ];

  updateableAttendanceStatusesMetadata = this.attendanceStatusMetadata.filter(
    (status) => status.statusCode !== EventAttendanceStatus.CANCELLED,
  );

  constructor(
    private http: HttpClient,
    private urlService: URLService,
    private translate: TranslateService,
  ) {}

  public eventAttendanceUpdate$(): Observable<Partial<EventAttendance>> {
    return this._eventAttendanceUpdate$.asObservable() as Observable<Partial<EventAttendance>>;
  }

  // Notify about attendance updates
  notifyAttendanceUpdate(update: Partial<EventAttendance> | null): void {
    this._eventAttendanceUpdate$.next(update);
  }

  attendEvent(eventId: string): Observable<EventAttendance> {
    return this.http.post<EventAttendance>(
      `${this.urlService.getDynamicUrl()}/attendance/attendee/${eventId}`,
      JSON.stringify({}),
      httpOptions,
    );
  }

  getEventAttendance(eventId: string): Observable<Partial<AttendanceUser>[]> {
    return this.http.get<Partial<AttendanceUser>[]>(
      `${this.urlService.getDynamicUrl()}/attendance/${eventId}`,
    );
  }

  updateUserAttendanceInEvent(
    eventId: string,
    attendance: Partial<EventAttendance>,
  ): Observable<any> {
    return this.http
      .post<any>(
        `${this.urlService.getDynamicUrl()}/attendance/host/${eventId}`,
        JSON.stringify({ attendance }),
        httpOptions,
      )
      .pipe(
        tap(() => {
          this.handleAttendanceUpdateSuccess(eventId, attendance);
        }),
      );
  }

  /**
   * Processes successful attendance update and notifies subscribers
   * @param eventId - The ID of the event
   * @param attendance - The attendance update data
   */
  private handleAttendanceUpdateSuccess(
    eventId: string,
    attendance: Partial<EventAttendance>,
  ): void {
    // Bug fix: notes was incorrectly assigned status instead of notes
    const updatedData: Partial<EventAttendance> = {
      event_id: eventId,
      user_id: attendance.user_id,
      ...(attendance.status && { status: attendance.status }),
      ...(attendance.notes !== undefined && { notes: attendance.notes }),
    };

    this.notifyAttendanceUpdate(updatedData);
  }
}
